// Coalesce duplicate/concurrent calls to the same request into a single in-flight promise,
// with an optional short TTL cache.
//
// Dedupes the common causes of identical repeated requests on a single page load:
//   - React StrictMode double-invoking a mount effect (dev)
//   - multiple components fetching the same endpoint during one render pass
//
// ttlMs = 0 (default): in-flight coalescing only — never serves stale data (safe for lists that
// change after user actions). ttlMs > 0: also serves a cached value for that window (use for
// static data like pricing packs).

type CacheEntry<T> = { at: number; data: T };

export function createCoalescedFetcher<T>(ttlMs = 0) {
  const cache = new Map<string, CacheEntry<T>>();
  const inflight = new Map<string, Promise<T>>();

  return function coalesced(key: string, fetcher: () => Promise<T>): Promise<T> {
    if (ttlMs > 0) {
      const hit = cache.get(key);
      if (hit && Date.now() - hit.at < ttlMs) return Promise.resolve(hit.data);
    }

    const existing = inflight.get(key);
    if (existing) return existing;

    const p = (async () => {
      try {
        const data = await fetcher();
        if (ttlMs > 0) cache.set(key, { at: Date.now(), data });
        return data;
      } finally {
        inflight.delete(key);
      }
    })();

    inflight.set(key, p);
    return p;
  };
}
